import { useRef, useCallback, useEffect, useState } from 'react';
import api from '../../utils/api';

// Leave browser connections free for interactive dictionary/status requests.
const BATCH_SIZE = 2;
const MAX_PRELOAD = 30;

/**
 * Audio preloader hook for Reader word tokens.
 * Preloads existing audio only. Cache misses generate speech only on Play.
 */
export function useAudioPreloader(languageCode, paused = false) {
  const cacheRef = useRef(new Map());     // word → blobURL
  const pendingRef = useRef(new Set());   // words currently being fetched
  const [preloadCount, setPreloadCount] = useState(0);
  const [preloading, setPreloading] = useState(false);
  const preloadController = useRef(null);

  const cancelPreload = useCallback(() => {
    preloadController.current?.abort();
    preloadController.current = null;
    pendingRef.current.clear();
    setPreloading(false);
  }, []);

  // Play a word from cache or fetch on demand
  const playWord = useCallback(async (word) => {
    if (!word || !languageCode) return;

    const key = `${word}__${languageCode}`;
    const cached = cacheRef.current.get(key);

    if (cached) {
      const audio = new Audio(cached);
      audio.play().catch(() => {});
      return;
    }

    // Not cached — fetch now (will also server-side cache)
    try {
      const res = await api.post('/tts/word', {
        word,
        language: languageCode,
        slow: false,
      }, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      cacheRef.current.set(key, blobUrl);
      setPreloadCount(cacheRef.current.size);

      const audio = new Audio(blobUrl);
      audio.play().catch(() => {});
    } catch {
      // Fallback to browser speechSynthesis
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
        const u = new SpeechSynthesisUtterance(word);
        u.lang = languageCode;
        u.rate = 0.85;
        window.speechSynthesis.speak(u);
      }
    }
  }, [languageCode]);

  // Preload a batch of words in the background
  const preloadWords = useCallback(async (words) => {
    if (!languageCode || !words?.length || paused) return;
    cancelPreload();
    const controller = new AbortController();
    preloadController.current = controller;

    // Filter to words not already cached or pending
    const toFetch = [];
    for (const w of new Set(words)) {
      const key = `${w}__${languageCode}`;
      if (!cacheRef.current.has(key) && !pendingRef.current.has(key)) {
        toFetch.push(w);
        if (toFetch.length >= MAX_PRELOAD) break;
      }
    }

    if (toFetch.length === 0) return;
    toFetch.forEach(word => pendingRef.current.add(`${word}__${languageCode}`));
    setPreloading(true);

    // Process in batches
    for (let i = 0; i < toFetch.length; i += BATCH_SIZE) {
      if (controller.signal.aborted) return;
      const batch = toFetch.slice(i, i + BATCH_SIZE);
      const promises = batch.map(async (word) => {
        const key = `${word}__${languageCode}`;
        pendingRef.current.add(key);
        try {
          const res = await api.post('/tts/word', {
            word,
            language: languageCode,
            slow: false,
            cache_only: true,
          }, { responseType: 'blob', signal: controller.signal, timeout: 15000 });
          if (controller.signal.aborted || res.status === 204) return;
          const blobUrl = URL.createObjectURL(res.data);
          cacheRef.current.set(key, blobUrl);
        } catch {
          // Silently skip failed preloads
        } finally {
          if (!controller.signal.aborted) pendingRef.current.delete(key);
        }
      });
      await Promise.all(promises);
      if (!controller.signal.aborted) setPreloadCount(cacheRef.current.size);
    }

    if (!controller.signal.aborted) setPreloading(false);
  }, [languageCode, paused, cancelPreload]);

  useEffect(() => {
    if (paused) cancelPreload();
    return cancelPreload;
  }, [paused, languageCode, cancelPreload]);

  // Check if a word is already preloaded
  const isPreloaded = useCallback((word) => {
    return cacheRef.current.has(`${word}__${languageCode}`);
  }, [languageCode]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      cacheRef.current.forEach((url) => URL.revokeObjectURL(url));
      cacheRef.current.clear();
    };
  }, []);

  return {
    playWord,
    preloadWords,
    cancelPreload,
    isPreloaded,
    preloading,
    preloadCount,
  };
}
